import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import type { IVScoreResponse } from "../../types/market-data";
import { FintheonNumericText } from "../numbers/FintheonNumericText";
import { FadingRuler } from "../shared/FadingRuler";
import { fuseSegmentColor } from "./iv-score-visuals";
import { IVScoreForecastFuses } from "./IVScoreForecastFuses";
import type { IvScorePopupPosition } from "./use-iv-score-tooltip";

interface IVScoreBreakdownProps {
  data: IVScoreResponse;
  marketDetail: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  position: IvScorePopupPosition;
}

export function IVScoreBreakdown(props: IVScoreBreakdownProps) {
  const { data } = props;
  const components = [
    {
      label: "VIX",
      value: data.vixComponent,
      detail: (
        <>
          VIX <FintheonNumericText text={data.vix.level.toFixed(1)} />
        </>
      ),
    },
    {
      label: "Headlines",
      value: data.headlineComponent,
      detail: (
        <>
          <FintheonNumericText text={data.eventCount} kind="count" /> events
        </>
      ),
    },
    { label: "AgentDesk", value: data.agentDeskComponent, detail: "Analysis" },
    ...(data.marketPerformance
      ? [
          {
            label: "Market",
            value:
              data.marketPerformanceComponent ??
              data.marketPerformance.ivComponent,
            detail: props.marketDetail,
          },
        ]
      : []),
  ];
  return createPortal(
    <section
      className="fintheon-popover-surface w-80 max-w-[90vw] overflow-y-auto p-3 text-[var(--fintheon-text)]/80 animate-tooltip-fade"
      data-iv-score-popup="agentic-breakdown"
      style={{ position: "fixed", ...props.position, zIndex: 9999 }}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
    >
      <h4 className="text-[12px] font-semibold text-[var(--fintheon-accent)]">
        Agentic Scoring Breakdown
      </h4>
      <div className="mt-2" data-iv-score-components="modernized">
        {components.map((component, index) => (
          <div key={component.label}>
            {index > 0 ? <FadingRuler className="opacity-35" /> : null}
            <ScoreComponent {...component} />
          </div>
        ))}
        <FadingRuler className="opacity-45" />
        <div className="flex items-center justify-between px-1 py-2">
          <span className="text-[10px] font-medium text-[var(--fintheon-text)]/72">
            Blended
          </span>
          <span className="font-mono text-xs font-bold text-[var(--fintheon-accent)]">
            <FintheonNumericText kind="score" text={data.score.toFixed(1)} />
            /10
          </span>
        </div>
      </div>
      {data.systemic && data.systemic.score > 0 ? (
        <>
          <FadingRuler className="opacity-45" />
          <SystemicRisk data={data.systemic} />
        </>
      ) : null}
      {data.prediction ? (
        <>
          <FadingRuler className="opacity-45" />
          <IVScoreForecastFuses prediction={data.prediction} />
        </>
      ) : null}
      <FadingRuler className="opacity-45" />
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-2 font-mono text-[8px] text-[var(--fintheon-muted)]/42">
        <span>Updated {new Date(data.timestamp).toLocaleTimeString()}</span>
        {data.vix.staleMinutes > 5 ? (
          <span className="text-[var(--fintheon-neutral-severe)]">
            VIX <FintheonNumericText text={data.vix.staleMinutes} />m old
          </span>
        ) : null}
      </div>
    </section>,
    document.body,
  );
}

function ScoreComponent(input: {
  label: string;
  value: number;
  detail: ReactNode;
}) {
  return (
    <div className="px-1 py-2">
      <div className="flex items-center justify-between gap-2 text-[10px]">
        <span className="text-[var(--fintheon-text)]/62">{input.label}</span>
        <span
          className="font-mono font-bold"
          style={{ color: fuseSegmentColor(Math.round(input.value)) }}
        >
          <FintheonNumericText kind="score" text={input.value.toFixed(1)} />
          /10
        </span>
      </div>
      <div className="mt-1 flex h-1.5 gap-px overflow-hidden rounded-full bg-[var(--fintheon-surface)]">
        {Array.from({ length: 10 }, (_, index) => (
          <span
            key={index}
            className="flex-1 rounded-full transition-[background-color,opacity] duration-200"
            style={{
              backgroundColor:
                index < Math.round(input.value)
                  ? fuseSegmentColor(index)
                  : "var(--fintheon-border)",
              opacity: index < Math.round(input.value) ? 1 : 0.35,
            }}
          />
        ))}
      </div>
      <p className="mt-1 text-[8px] text-[var(--fintheon-muted)]/42">
        {input.detail}
      </p>
    </div>
  );
}

function SystemicRisk({
  data,
}: {
  data: NonNullable<IVScoreResponse["systemic"]>;
}) {
  return (
    <div className="py-2 text-[10px]">
      <div className="flex items-center justify-between">
        <span className="font-medium text-[var(--fintheon-high)]">
          Systemic Risk
        </span>
        <span className="font-mono text-[var(--fintheon-high)]">
          <FintheonNumericText kind="score" text={data.score.toFixed(1)} />
          /10 · +<FintheonNumericText text={data.overlay.toFixed(1)} /> IV
        </span>
      </div>
      <p className="mt-1 text-[var(--fintheon-muted)]/52">
        <FintheonNumericText text={data.activeChains} kind="count" /> causal
        chains, <FintheonNumericText text={data.creditSignals} kind="count" />{" "}
        credit signals
      </p>
      {data.topRhyme ? (
        <p className="mt-1 text-[var(--fintheon-text)]/62">
          <FintheonNumericText
            text={Math.round(data.topRhyme.matchScore * 100)}
            kind="percentage-change"
          />
          % match to{" "}
          <FintheonNumericText
            text={data.topRhyme.crisisYear}
            kind="date-time"
          />{" "}
          {data.topRhyme.crisisName}
        </p>
      ) : null}
    </div>
  );
}
